import React, { useRef } from 'react';
import html2pdf from 'html2pdf.js';

export default function FacturaPDF({ factura, onClose }) {
  const pdfRef = useRef(null);

  const handleDownload = () => {
    const element = pdfRef.current;
    const opt = {
      margin: 0,
      filename: `Factura-${factura.numeroFactura}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    html2pdf().set(opt).from(element).save();
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('es-AR');
  };

  const formatCurrency = (amount) => {
    return Number(amount).toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const tipoFactura = factura.tipo || 'A';
  const esFantasia = factura.esFantasia;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-8">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 rounded-t-xl">
          <h2 className="text-lg font-bold text-gray-900">Vista previa - Factura {factura.numeroFactura}</h2>
          <div className="flex space-x-2">
            <button
              onClick={handleDownload}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
            >
              Descargar PDF
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg text-sm font-medium"
            >
              Cerrar
            </button>
          </div>
        </div>

        {/* Contenido del PDF */}
        <div ref={pdfRef} className="p-8 bg-white" style={{ width: '210mm', minHeight: '297mm' }}>
          {/* Encabezado */}
          <div className="border-2 border-gray-800 mb-4">
            <div className="flex">
              {/* Tipo de Factura */}
              <div className="w-24 border-r-2 border-gray-800 flex items-center justify-center">
                <div className="text-5xl font-bold text-gray-800">{tipoFactura}</div>
              </div>

              <div className="flex-1 p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">PilarNet S.A.</h1>
                    <p className="text-sm text-gray-600">Razon Social: PilarNet S.A.</p>
                    <p className="text-sm text-gray-600">Domicilio: Pilar, Buenos Aires</p>
                    <p className="text-sm text-gray-600">Condicion IVA: Responsable Inscripto</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-gray-900">ORIGINAL</p>
                    {esFantasia && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-bold rounded">
                        FACTURA DE FANTASIA
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Info Factura */}
          <div className="border-2 border-gray-800 mb-4 p-3">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-bold">Punto de Venta:</span> 0001
              </div>
              <div>
                <span className="font-bold">Comp. Nro:</span> {factura.numeroFactura?.split('-').pop() || '-'}
              </div>
              <div>
                <span className="font-bold">Fecha de Emision:</span> {formatDate(factura.fechaEmision)}
              </div>
            </div>
          </div>

          {/* Cliente */}
          <div className="border-2 border-gray-800 mb-4 p-3">
            <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase border-b border-gray-300 pb-1">
              Datos del Cliente
            </h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="font-bold">Apellido y Nombre / Razon Social:</span></div>
              <div>{factura.cliente?.apellido} {factura.cliente?.nombre}</div>

              <div><span className="font-bold">Domicilio:</span></div>
              <div>{factura.cliente?.direccion || '-'}</div>

              <div><span className="font-bold">Condicion frente al IVA:</span></div>
              <div>{factura.cliente?.condicionIva || 'Consumidor Final'}</div>

              <div><span className="font-bold">DNI / CUIT:</span></div>
              <div>{factura.cliente?.dniCuit || '-'}</div>
            </div>
          </div>

          {/* Items */}
          <div className="border-2 border-gray-800 mb-4">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 border-b-2 border-gray-800">
                <tr>
                  <th className="px-3 py-2 text-left font-bold">Codigo</th>
                  <th className="px-3 py-2 text-left font-bold">Producto / Servicio</th>
                  <th className="px-3 py-2 text-right font-bold">Cantidad</th>
                  <th className="px-3 py-2 text-right font-bold">Precio Unit.</th>
                  <th className="px-3 py-2 text-right font-bold">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {factura.items?.map((item, index) => (
                  <tr key={index} className="border-b border-gray-200">
                    <td className="px-3 py-2">SRV{String(index + 1).padStart(3, '0')}</td>
                    <td className="px-3 py-2">{item.concepto}</td>
                    <td className="px-3 py-2 text-right">{item.cantidad}</td>
                    <td className="px-3 py-2 text-right">${formatCurrency(item.precioUnitario)}</td>
                    <td className="px-3 py-2 text-right">${formatCurrency(item.subtotal)}</td>
                  </tr>
                )) || (
                  <tr>
                    <td className="px-3 py-2" colSpan="5">Sin items</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totales */}
          <div className="flex justify-end mb-4">
            <div className="w-64 border-2 border-gray-800 p-3">
              <div className="flex justify-between text-sm mb-1">
                <span className="font-bold">Subtotal:</span>
                <span>${formatCurrency(factura.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-bold">IVA ({factura.ivaPorcentaje || 21}%):</span>
                <span>${formatCurrency(factura.ivaMonto)}</span>
              </div>
              <div className="flex justify-between text-base font-bold border-t-2 border-gray-800 pt-2 mt-2">
                <span>TOTAL:</span>
                <span>${formatCurrency(factura.total)}</span>
              </div>
            </div>
          </div>

          {/* Observaciones */}
          {factura.observaciones && (
            <div className="border-2 border-gray-800 p-3 mb-4">
              <p className="text-sm font-bold text-gray-700 mb-1">Observaciones:</p>
              <p className="text-sm text-gray-600">{factura.observaciones}</p>
            </div>
          )}

          {/* CAE (si es ARCA) */}
          {!esFantasia && (
            <div className="border-2 border-gray-800 p-3 mb-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-bold">CAE:</span> {factura.cae || 'PENDIENTE'}
                </div>
                <div>
                  <span className="font-bold">Vencimiento CAE:</span> {formatDate(factura.vencimientoCae)}
                </div>
              </div>
            </div>
          )}

          {/* Pie */}
          <div className="text-center text-xs text-gray-500 mt-8 pt-4 border-t border-gray-300">
            <p>Esta factura fue generada electronicamente por PilarNet S.A.</p>
            <p>Para consultas: admin@pilarnet.online</p>
          </div>
        </div>
      </div>
    </div>
  );
}
